const option = (value, label) => ({ value, label });

export const RESOURCE_CONFIG = {
  categories: {
    group: "Catalogue",
    title: "Recipe categories",
    note: "Manage the hierarchy and artwork used to organise recipes.",
    singular: "category",
    help: {
      text: "Categories group recipes in discovery and can be nested under another category.",
      items: [
        "Use a concise title and app-facing tagline.",
        "Display order follows the backend index ordering; higher values appear first where that ordering is used.",
        "Choose a parent only for a nested category, and upload an icon for every new category.",
        "Review attached recipes and home-section items before deleting a category.",
      ],
    },
    fileField: "icon",
    fields: [
      { key: "title", label: "Title", required: true },
      { key: "tagline", label: "Tagline", type: "textarea", required: true },
      { key: "index", label: "Display order", type: "number", default: 0 },
      {
        key: "category",
        label: "Parent category",
        type: "resource",
        resource: "categories",
        nullable: true,
      },
      { key: "icon", label: "Category icon", type: "file", accept: "image/*" },
    ],
    columns: [
      { key: "icon", label: "Icon", type: "image" },
      { key: "title", label: "Category" },
      { key: "category_label", label: "Parent" },
      { key: "index", label: "Order" },
    ],
  },
  ingredients: {
    group: "Catalogue",
    title: "Ingredient master",
    note: "Maintain the reusable ingredient vocabulary used by recipe rows.",
    singular: "ingredient",
    help: {
      text: "Ingredient master records are reusable references, not recipe-specific quantities.",
      items: [
        "Search before adding to avoid duplicate ingredient names.",
        "Regional names make alternate Indian names easier for admins to find.",
        "Allergen tags power safety filters; select every allergen that applies.",
        "Deleting a master ingredient also affects recipe ingredient rows that reference it.",
      ],
    },
    fileField: "image",
    fields: [
      { key: "title", label: "Ingredient name", required: true },
      { key: "indian_names", label: "Indian / regional names", type: "textarea" },
      {
        key: "allergens",
        label: "Allergen tags",
        type: "multi-resource",
        resource: "tags",
      },
      { key: "image", label: "Ingredient image", type: "file", accept: "image/*" },
    ],
    columns: [
      { key: "image", label: "Image", type: "image" },
      { key: "title", label: "Ingredient" },
      { key: "indian_names", label: "Regional names" },
      { key: "allergens_labels", label: "Allergens", type: "list" },
    ],
  },
  recipeIngredients: {
    group: "Recipe content",
    title: "Recipe ingredients",
    note: "Edit individual recipe-to-ingredient rows and scaling metadata.",
    singular: "recipe ingredient",
    help: {
      text: "Each row connects one recipe with one ingredient master record.",
      items: [
        "Use Position to control ingredient order in the cooking view.",
        "Quantity is the readable fallback; quantity value and unit enable serving-size scaling.",
        "Disable Scalable for ingredients such as garnish that should not scale automatically.",
        "The guided recipe editor can update all rows together; use this section for precise corrections.",
      ],
    },
    fields: [
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "ingredients", label: "Ingredient", type: "resource", resource: "ingredients", required: true },
      { key: "quantity", label: "Readable quantity" },
      { key: "help_text", label: "Preparation note", type: "textarea" },
      { key: "position", label: "Position", type: "number", default: 0 },
      { key: "quantity_value", label: "Quantity value", type: "number", step: "0.01", nullable: true },
      { key: "quantity_unit", label: "Quantity unit" },
      { key: "is_scalable", label: "Scales with servings", type: "checkbox", default: true },
    ],
    columns: [
      { key: "recipe_label", label: "Recipe" },
      { key: "ingredients_label", label: "Ingredient" },
      { key: "quantity", label: "Quantity" },
      { key: "position", label: "Position" },
      { key: "is_scalable", label: "Scalable", type: "boolean" },
    ],
  },
  directions: {
    group: "Recipe content",
    title: "Recipe directions",
    note: "Manage individual ordered cooking instructions and timers.",
    singular: "direction",
    help: {
      text: "Directions are the step-by-step cooking flow for a recipe.",
      items: [
        "Step numbers must be unique within a recipe and determine display order.",
        "Instructions support rich HTML produced by the recipe editor.",
        "Duration in seconds enables timers; timer label explains what the timer is for.",
        "Use the guided recipe editor when reordering several directions together.",
      ],
    },
    fields: [
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "step", label: "Step number", type: "number", default: 0, required: true },
      { key: "head_name", label: "Step heading" },
      { key: "Directions", label: "Instructions", type: "textarea", required: true },
      { key: "duration_seconds", label: "Timer duration (seconds)", type: "number", nullable: true },
      { key: "timer_label", label: "Timer label" },
    ],
    columns: [
      { key: "recipe_label", label: "Recipe" },
      { key: "step", label: "Step" },
      { key: "head_name", label: "Heading" },
      { key: "duration_seconds", label: "Timer (sec)" },
    ],
  },
  nutrition: {
    group: "Recipe content",
    title: "Recipe nutrition",
    note: "Maintain the one nutrition profile allowed for each recipe.",
    singular: "nutrition profile",
    help: {
      text: "Nutrition combines legacy display values with structured per-serving metrics.",
      items: [
        "Only one nutrition record can exist for each recipe.",
        "Use serving size to describe the basis for all numeric values.",
        "Structured gram and daily-value fields power the current mobile recipe experience.",
        "Keep legacy kCal, protein, fat, and carbs populated while older clients still use them.",
      ],
    },
    fields: [
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "serving_size", label: "Serving size" },
      { key: "calories_kcal", label: "Calories (kcal)", type: "number", nullable: true },
      { key: "protein_g", label: "Protein (g)", type: "number", step: "0.01", nullable: true },
      { key: "fat_g", label: "Fat (g)", type: "number", step: "0.01", nullable: true },
      { key: "carbs_g", label: "Carbs (g)", type: "number", step: "0.01", nullable: true },
      { key: "fiber_g", label: "Fiber (g)", type: "number", step: "0.01", nullable: true },
      { key: "iron_dv_percent", label: "Iron daily value (%)", type: "number", step: "0.01", nullable: true },
      { key: "vitamin_a_dv_percent", label: "Vitamin A daily value (%)", type: "number", step: "0.01", nullable: true },
      { key: "kCal", label: "Legacy kCal", type: "number", default: 0 },
      { key: "protein", label: "Legacy protein" },
      { key: "fat", label: "Legacy fat" },
      { key: "carbs", label: "Legacy carbs" },
    ],
    columns: [
      { key: "recipe_label", label: "Recipe" },
      { key: "serving_size", label: "Serving" },
      { key: "calories_kcal", label: "Calories" },
      { key: "protein_g", label: "Protein (g)" },
      { key: "fat_g", label: "Fat (g)" },
      { key: "carbs_g", label: "Carbs (g)" },
    ],
  },
  tags: {
    group: "Discovery",
    title: "Recipe tags",
    note: "Manage feature, goal, allergen, and dietary labels.",
    singular: "tag",
    help: {
      text: "Tags classify recipes and ingredients for discovery, goals, dietary rules, and allergens.",
      items: [
        "Slug is a stable machine identifier; avoid changing it after app integrations use it.",
        "Kind determines where the tag can be used. Ingredient allergens should use Allergen.",
        "Disable a tag to retire it without breaking existing relationships.",
        "Assign active tags from the recipe editor or ingredient master.",
      ],
    },
    fields: [
      { key: "label", label: "Label", required: true },
      { key: "slug", label: "Slug", required: true },
      {
        key: "kind",
        label: "Kind",
        type: "select",
        required: true,
        options: [option("feature", "Feature"), option("goal", "Goal"), option("allergen", "Allergen"), option("dietary", "Dietary")],
      },
      { key: "is_active", label: "Active", type: "checkbox", default: true },
    ],
    columns: [
      { key: "label", label: "Label" },
      { key: "slug", label: "Slug" },
      { key: "kind_display", label: "Kind" },
      { key: "is_active", label: "Active", type: "boolean" },
    ],
  },
  chips: {
    group: "Discovery",
    title: "Recipe filter chips",
    note: "Control backend-driven shortcuts shown in recipe discovery.",
    singular: "filter chip",
    help: {
      text: "Filter chips translate a user-facing label into a Recipe field lookup.",
      items: [
        "Internal name must be unique and stable; display name is what users see.",
        "Field lookup must match a valid Recipe lookup, such as diet_type or category__title.",
        "Operator controls the comparison; use eq for exact matches and lt/lte/gt/gte for ranges.",
        "Disable Visible to test or retire a chip without deleting it.",
      ],
    },
    fields: [
      { key: "name", label: "Internal name", required: true },
      { key: "display_name", label: "Display label", required: true },
      { key: "field_name", label: "Recipe field lookup", required: true },
      { key: "operator", label: "Operator", type: "select", options: ["eq", "lt", "lte", "gt", "gte", "icontains"], default: "eq" },
      { key: "value", label: "Comparison value", required: true },
      { key: "visible", label: "Visible in app", type: "checkbox", default: true },
    ],
    columns: [
      { key: "display_name", label: "Display label" },
      { key: "name", label: "Internal name" },
      { key: "field_name", label: "Field" },
      { key: "operator", label: "Operator" },
      { key: "value", label: "Value" },
      { key: "visible", label: "Visible", type: "boolean" },
    ],
  },
  collections: {
    group: "Discovery",
    title: "Recipe collections",
    note: "Curate ordered recipe groups used by collection cards and sections.",
    singular: "collection",
    help: {
      text: "Collections are reusable editorial groups with their own title, artwork, and order.",
      items: [
        "Slug is used by app routes and should remain stable once published.",
        "Cover image represents the collection in discovery.",
        "Use Collection items to add recipes and control their order.",
        "Disable a collection to hide it without deleting its curated items.",
      ],
    },
    fileField: "cover_image",
    fields: [
      { key: "title", label: "Title", required: true },
      { key: "slug", label: "Slug", required: true },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
      { key: "order", label: "Display order", type: "number", default: 0 },
      { key: "is_active", label: "Active", type: "checkbox", default: true },
      { key: "cover_image", label: "Cover image", type: "file", accept: "image/*" },
    ],
    columns: [
      { key: "cover_image", label: "Cover", type: "image" },
      { key: "title", label: "Collection" },
      { key: "slug", label: "Slug" },
      { key: "order", label: "Order" },
      { key: "is_active", label: "Active", type: "boolean" },
    ],
  },
  collectionItems: {
    group: "Discovery",
    title: "Recipe collection items",
    note: "Choose which recipes belong to a collection and their order.",
    singular: "collection item",
    help: {
      text: "Each collection item connects one recipe to one collection.",
      items: [
        "A recipe can appear only once within the same collection.",
        "Lower order values appear first.",
        "Changing the collection moves the item without changing the recipe.",
        "Deleting an item removes it from the collection but does not delete the recipe.",
      ],
    },
    fields: [
      { key: "collection", label: "Collection", type: "resource", resource: "collections", required: true },
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "order", label: "Order", type: "number", default: 0 },
    ],
    columns: [
      { key: "collection_label", label: "Collection" },
      { key: "recipe_label", label: "Recipe" },
      { key: "order", label: "Order" },
    ],
  },
  homeSections: {
    group: "Home experience",
    title: "Recipe home sections",
    note: "Configure the ordered modules rendered on recipe home.",
    singular: "home section",
    help: {
      text: "Home sections define the kind, layout, schedule, and audience rules for each recipe-home module.",
      items: [
        "Section kind and layout must accept the same target type; the backend rejects mismatches.",
        "Start and end times schedule visibility; leave both blank for always-on sections.",
        "Personalize lets the mobile experience rank eligible content for the current family.",
        "Meal-plan CTA sections do not accept section items; other kinds use Home section items.",
      ],
    },
    fields: [
      { key: "title", label: "Title", required: true },
      { key: "slug", label: "Slug", required: true },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
      { key: "kind", label: "Kind", type: "select", options: [option("hero", "Hero"), option("categories", "Categories"), option("continue_cooking", "Continue cooking"), option("personalized", "Personalized"), option("trending", "Trending"), option("editors_picks", "Editor's picks"), option("collections", "Collections"), option("chefs", "Chefs"), option("meal_plan", "Meal plan")], required: true },
      { key: "layout", label: "Layout", type: "select", options: [option("hero_carousel", "Hero carousel"), option("horizontal_cards", "Horizontal cards"), option("compact_cards", "Compact cards"), option("category_chips", "Category chips"), option("collection_cards", "Collection cards"), option("chef_cards", "Chef cards"), option("meal_plan_card", "Meal plan card")], default: "horizontal_cards", required: true },
      { key: "order", label: "Display order", type: "number", default: 0 },
      { key: "max_items", label: "Maximum items", type: "number", default: 12 },
      { key: "enabled", label: "Enabled", type: "checkbox", default: true },
      { key: "personalize", label: "Personalize", type: "checkbox", default: false },
      { key: "starts_at", label: "Starts at", type: "datetime-local", nullable: true },
      { key: "ends_at", label: "Ends at", type: "datetime-local", nullable: true },
      { key: "minimum_age_months", label: "Minimum age (months)", type: "number", nullable: true },
      { key: "maximum_age_months", label: "Maximum age (months)", type: "number", nullable: true },
    ],
    columns: [
      { key: "title", label: "Section" },
      { key: "kind_display", label: "Kind" },
      { key: "layout_display", label: "Layout" },
      { key: "order", label: "Order" },
      { key: "enabled", label: "Enabled", type: "boolean" },
      { key: "personalize", label: "Personalized", type: "boolean" },
    ],
  },
  homeSectionItems: {
    group: "Home experience",
    title: "Recipe home section items",
    note: "Curate the exact content and campaign copy inside home sections.",
    singular: "home section item",
    help: {
      text: "A section item targets exactly one recipe, category, collection, or chef.",
      items: [
        "Choose only the target type accepted by the selected section's kind and layout.",
        "Order controls position inside the section; Enabled hides an item without deleting it.",
        "Campaign copy and image can override recipe-card presentation for editorial hero content.",
        "CTA URL accepts only supported recipe routes and MyBurgo recipe deep links.",
      ],
    },
    fileField: "campaign_image",
    fields: [
      { key: "section", label: "Home section", type: "resource", resource: "homeSections", required: true },
      { key: "recipe", label: "Recipe target", type: "resource", resource: "recipes", nullable: true },
      { key: "category", label: "Category target", type: "resource", resource: "categories", nullable: true },
      { key: "collection", label: "Collection target", type: "resource", resource: "collections", nullable: true },
      { key: "chef", label: "Chef target", type: "resource", resource: "experts", nullable: true },
      { key: "order", label: "Order", type: "number", default: 0 },
      { key: "enabled", label: "Enabled", type: "checkbox", default: true },
      { key: "kicker", label: "Kicker" },
      { key: "headline", label: "Headline" },
      { key: "subheadline", label: "Subheadline", type: "textarea" },
      { key: "cta_label", label: "CTA label" },
      { key: "cta_url", label: "CTA route / deep link" },
      { key: "campaign_image", label: "Campaign image", type: "file", accept: "image/*" },
    ],
    columns: [
      { key: "section_label", label: "Section" },
      { key: "recipe_label", label: "Recipe" },
      { key: "category_label", label: "Category" },
      { key: "collection_label", label: "Collection" },
      { key: "chef_label", label: "Chef" },
      { key: "order", label: "Order" },
      { key: "enabled", label: "Enabled", type: "boolean" },
    ],
  },
  views: {
    group: "Engagement",
    title: "Recipe views",
    note: "Inspect or correct per-user recipe view counters.",
    singular: "view record",
    help: {
      text: "A view record tracks how many times one user viewed one recipe.",
      items: [
        "Use the exact AppUser ID from the Users admin section.",
        "Only one view record can exist for the same user and recipe.",
        "Last viewed is updated by the backend; view counter can be corrected here.",
        "The per-user counter is separate from the recipe catalogue's total view count.",
      ],
    },
    fields: [
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "user", label: "AppUser ID", type: "number", required: true },
      { key: "view_counter", label: "View counter", type: "number", default: 0 },
    ],
    columns: [
      { key: "recipe_label", label: "Recipe" },
      { key: "user_label", label: "User" },
      { key: "view_counter", label: "Views" },
      { key: "last_view", label: "Last viewed", type: "dateTime" },
    ],
  },
  favourites: {
    group: "Engagement",
    title: "Recipe favourites",
    note: "Manage user-to-recipe saved-favourite records.",
    singular: "favourite",
    help: {
      text: "A favourite links one AppUser to one recipe saved in their favourites.",
      items: [
        "Use the exact AppUser ID and choose the intended recipe.",
        "The backend prevents duplicate favourites for the same user and recipe.",
        "Favourite time is managed automatically.",
        "Deleting the record removes the recipe from that user's saved favourites.",
      ],
    },
    fields: [
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "user", label: "AppUser ID", type: "number", required: true },
    ],
    columns: [
      { key: "recipe_label", label: "Recipe" },
      { key: "user_label", label: "User" },
      { key: "favourite_time", label: "Favourited", type: "dateTime" },
    ],
  },
  likes: {
    group: "Engagement",
    title: "Recipe likes",
    note: "Manage unique user-to-recipe like records.",
    singular: "like",
    help: {
      text: "A like is a unique relationship between one AppUser and one recipe.",
      items: [
        "Choose the recipe and enter the exact AppUser ID.",
        "The backend prevents the same user from liking the same recipe twice.",
        "Deleting a record immediately removes that user's like.",
        "Use manual changes only for moderation, support, or data correction.",
      ],
    },
    fields: [
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "user", label: "AppUser ID", type: "number", required: true },
    ],
    columns: [
      { key: "recipe_label", label: "Recipe" },
      { key: "user_label", label: "User" },
      { key: "created", label: "Liked", type: "dateTime" },
    ],
  },
  interactions: {
    group: "Engagement",
    title: "Recipe interactions",
    note: "Inspect or correct the event stream used for analytics and personalisation.",
    singular: "interaction",
    help: {
      text: "Interactions record impressions, opens, saves, dismissals, and cooking events.",
      items: [
        "User and recipe are required; child is optional when the event is family-profile specific.",
        "Client event ID makes mobile event ingestion idempotent for a user.",
        "Section slug attributes discovery events to the home section that produced them.",
        "Metadata must be valid JSON. Change analytics events only for deliberate support corrections.",
      ],
    },
    fields: [
      { key: "user", label: "AppUser ID", type: "number", required: true },
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "child", label: "Child ID", type: "number", nullable: true },
      { key: "event_type", label: "Event type", type: "select", options: [option("impression", "Impression"), option("open", "Open"), option("save", "Save"), option("unsave", "Unsave"), option("dismiss", "Dismiss"), option("cook_start", "Cook start"), option("cook_complete", "Cook complete")], required: true },
      { key: "client_event_id", label: "Client event ID", nullable: true },
      { key: "section_slug", label: "Section slug" },
      { key: "metadata", label: "Metadata JSON", type: "json", default: {} },
    ],
    columns: [
      { key: "user_label", label: "User" },
      { key: "recipe_label", label: "Recipe" },
      { key: "event_type_display", label: "Event" },
      { key: "section_slug", label: "Section" },
      { key: "created", label: "Recorded", type: "dateTime" },
    ],
  },
  cookSessions: {
    group: "Cooking & plans",
    title: "Recipe cook sessions",
    note: "Manage active, completed, and abandoned guided-cooking sessions.",
    singular: "cook session",
    help: {
      text: "Cook sessions preserve a user's step, checked ingredients, serving count, and completion state.",
      items: [
        "Only one in-progress session is allowed per user, recipe, and child context.",
        "Checked ingredient IDs must be a valid JSON array of recipe-ingredient IDs.",
        "Rating accepts 1 to 5 after cooking is completed.",
        "Started and last-activity times are managed automatically; set completed time when correcting status.",
      ],
    },
    fields: [
      { key: "user", label: "AppUser ID", type: "number", required: true },
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "child", label: "Child ID", type: "number", nullable: true },
      { key: "status", label: "Status", type: "select", options: [option("in_progress", "In progress"), option("completed", "Completed"), option("abandoned", "Abandoned")], default: "in_progress" },
      { key: "servings", label: "Servings", type: "number", default: 1 },
      { key: "current_step", label: "Current step", type: "number", default: 0 },
      { key: "checked_ingredient_ids", label: "Checked ingredient IDs JSON", type: "json", default: [] },
      { key: "rating", label: "Rating (1–5)", type: "number", nullable: true },
      { key: "completed_at", label: "Completed at", type: "datetime-local", nullable: true },
    ],
    columns: [
      { key: "id", label: "Session ID" },
      { key: "user_label", label: "User" },
      { key: "recipe_label", label: "Recipe" },
      { key: "status_display", label: "Status" },
      { key: "current_step", label: "Step" },
      { key: "last_activity_at", label: "Last activity", type: "dateTime" },
    ],
  },
  mealPlans: {
    group: "Cooking & plans",
    title: "Recipe meal plans",
    note: "Manage generated and saved plans, preferences, and generation status.",
    singular: "meal plan",
    help: {
      text: "A meal plan belongs to a user, optionally a child, and owns ordered meal-plan items.",
      items: [
        "Days must be from 1 to 14; meal slots, goals, and avoid tags are JSON arrays.",
        "Generation source and status explain whether deterministic or AI planning produced the plan.",
        "Idempotency key prevents duplicate generation requests for a user.",
        "Use Meal plan items to edit the recipe assigned to each day and slot.",
      ],
    },
    fields: [
      { key: "user", label: "AppUser ID", type: "number", required: true },
      { key: "child", label: "Child ID", type: "number", nullable: true },
      { key: "title", label: "Title", default: "My meal plan", required: true },
      { key: "start_date", label: "Start date", type: "date", required: true },
      { key: "days", label: "Days", type: "number", default: 7 },
      { key: "meal_slots", label: "Meal slots JSON", type: "json", default: [] },
      { key: "goals", label: "Goals JSON", type: "json", default: [] },
      { key: "avoid_tags", label: "Avoid tags JSON", type: "json", default: [] },
      { key: "note", label: "Note", type: "textarea" },
      { key: "reminders_enabled", label: "Reminders enabled", type: "checkbox", default: false },
      { key: "idempotency_key", label: "Idempotency key" },
      { key: "status", label: "Status", type: "select", options: [option("draft", "Draft"), option("saved", "Saved")], default: "draft" },
      { key: "generation_source", label: "Generation source", type: "select", options: [option("deterministic", "Deterministic"), option("ai", "AI")], default: "deterministic" },
      { key: "generation_status", label: "Generation status", type: "select", options: [option("pending", "Pending"), option("ready", "Ready"), option("failed", "Failed")], default: "ready" },
      { key: "is_saved", label: "Saved", type: "checkbox", default: false },
      { key: "saved_at", label: "Saved at", type: "datetime-local", nullable: true },
    ],
    columns: [
      { key: "title", label: "Plan" },
      { key: "user_label", label: "User" },
      { key: "start_date", label: "Starts" },
      { key: "days", label: "Days" },
      { key: "generation_source_display", label: "Source" },
      { key: "generation_status_display", label: "Generation" },
      { key: "is_saved", label: "Saved", type: "boolean" },
    ],
  },
  mealPlanItems: {
    group: "Cooking & plans",
    title: "Recipe meal plan items",
    note: "Edit each day-and-slot assignment inside a meal plan.",
    singular: "meal plan item",
    help: {
      text: "Each item assigns one recipe to one day and meal slot in a plan.",
      items: [
        "A plan can contain only one item for the same day index and meal slot.",
        "Date should match the plan start date plus the day index.",
        "Swapped from records the recipe replaced by a user or admin swap.",
        "Reminder sent time is normally written by the notification task.",
      ],
    },
    fields: [
      { key: "plan", label: "Meal plan", type: "resource", resource: "mealPlans", required: true },
      { key: "day_index", label: "Day index", type: "number", required: true },
      { key: "date", label: "Date", type: "date", required: true },
      { key: "meal_slot", label: "Meal slot", required: true },
      { key: "recipe", label: "Recipe", type: "resource", resource: "recipes", required: true },
      { key: "reason", label: "Reason", type: "textarea" },
      { key: "servings", label: "Servings", type: "number", default: 1 },
      { key: "swapped_from", label: "Swapped from", type: "resource", resource: "recipes", nullable: true },
      { key: "reminder_sent_at", label: "Reminder sent at", type: "datetime-local", nullable: true },
    ],
    columns: [
      { key: "plan_label", label: "Plan" },
      { key: "day_index", label: "Day" },
      { key: "meal_slot", label: "Slot" },
      { key: "recipe_label", label: "Recipe" },
      { key: "servings", label: "Servings" },
      { key: "swapped_from_label", label: "Swapped from" },
    ],
  },
  experienceConfigurations: {
    group: "Configuration",
    title: "Recipe experience configurations",
    note: "Control mobile copy, options, personalisation, filters, and sorts.",
    singular: "experience configuration",
    help: {
      text: "The current experience configuration is the admin-owned contract consumed by mobile recipe flows.",
      items: [
        "Only one non-deleted configuration can be current at a time.",
        "Every JSON field is validated against supported values and shapes by the backend.",
        "Leave JSON fields blank when creating to use production defaults, then edit the generated configuration.",
        "Test app discovery, meal-plan preferences, and copy after changing the current configuration.",
      ],
    },
    fields: [
      { key: "name", label: "Name", default: "Default recipe experience", required: true },
      { key: "is_current", label: "Current configuration", type: "checkbox", default: false },
      { key: "meal_slots", label: "Meal slots JSON", type: "json", omitWhenBlank: true },
      { key: "avoidances", label: "Avoidances JSON", type: "json", omitWhenBlank: true },
      { key: "goals", label: "Goals JSON", type: "json", omitWhenBlank: true },
      { key: "ui_copy", label: "UI copy JSON", type: "json", omitWhenBlank: true },
      { key: "personalization_rules", label: "Personalisation rules JSON", type: "json", omitWhenBlank: true },
      { key: "discovery_filters", label: "Discovery filters JSON", type: "json", omitWhenBlank: true },
      { key: "discovery_sorts", label: "Discovery sorts JSON", type: "json", omitWhenBlank: true },
    ],
    columns: [
      { key: "name", label: "Configuration" },
      { key: "is_current", label: "Current", type: "boolean" },
      { key: "modified", label: "Last updated", type: "dateTime" },
    ],
  },
  mealPlanAIConfigurations: {
    group: "Configuration",
    title: "Recipe meal-plan AI configurations",
    note: "Control optional AI planning while retaining deterministic fallback.",
    singular: "AI configuration",
    help: {
      text: "AI configuration controls whether and how the meal planner may use an AI model.",
      items: [
        "Enabled allows AI planning; Is current selects the one active AI configuration.",
        "Prompt template must instruct the model to choose only supplied candidate recipe IDs.",
        "Timeout and candidate limits protect latency and cost.",
        "The deterministic planner remains available as a fallback when AI is disabled or fails.",
      ],
    },
    fields: [
      { key: "name", label: "Name", default: "Recipe meal planner", required: true },
      { key: "enabled", label: "Enabled", type: "checkbox", default: false },
      { key: "is_current", label: "Current configuration", type: "checkbox", default: false },
      { key: "model", label: "Model", default: "gpt-4.1-mini", required: true },
      { key: "prompt_template", label: "Prompt template", type: "textarea", required: true, default: "Choose safe, varied recipes from the supplied candidates. Return only candidate recipe IDs in the requested day and meal-slot order." },
      { key: "version", label: "Version", default: "v1", required: true },
      { key: "timeout_seconds", label: "Timeout (seconds)", type: "number", default: 8 },
      { key: "max_candidates", label: "Maximum candidates", type: "number", default: 80 },
    ],
    columns: [
      { key: "name", label: "Configuration" },
      { key: "model", label: "Model" },
      { key: "version", label: "Version" },
      { key: "enabled", label: "Enabled", type: "boolean" },
      { key: "is_current", label: "Current", type: "boolean" },
      { key: "modified", label: "Last updated", type: "dateTime" },
    ],
  },
};

export const RESOURCE_TABS = Object.entries(RESOURCE_CONFIG).map(
  ([key, config]) => ({ key, label: config.title, group: config.group })
);
